"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  setEhrTokens,
  hospitalAuthApi,
} from "@/lib/api/ehr-client";
import type { HospitalLoginResponse } from "@/types/ehr";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "/api";

// ============================================================================
// System admin login
// ============================================================================
async function systemLogin(email: string, password: string) {
  const res = await fetch(`${API_BASE_URL}/users/login/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Login failed");
  }
  return res.json();
}

function systemRedirectPath(user: any): string {
  const role =
    user?.permissions?.role ||
    user?.role?.role_name ||
    "";
  if (role === "superadmin") return "/dashboard";
  if (role === "admin" || role === "manager" || role === "support")
    return "/dashboard";
  return "/dashboard";
}

// ============================================================================
// Login page
// ============================================================================
export default function LoginPage() {
  const router = useRouter();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // =========================================================================
  // Step 1: Login
  // =========================================================================
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Try hospital user login first
      let isHospitalUser = false;
      try {
        const resp = await hospitalAuthApi.login(email, password);
        isHospitalUser = true;

        const userProfile = {
          id: resp.user_id,
          email: resp.email,
          full_name: resp.full_name,
          role: resp.role,
          hospital_id: resp.hospital_id,
          hospital_name: resp.hospital_name,
          hospital_short_code: resp.hospital_short_code,
          hospital_slug: resp.hospital_slug,
          hospital_type: resp.hospital_type,
          location_id: resp.location_id,
          location_slug: resp.location_slug ?? null,
          is_active: true,
          must_change_password: resp.must_change_password,
          consult_permissions: resp.consult_permissions ?? null,
        };

        setEhrTokens(resp.access, resp.refresh);
        localStorage.setItem("ehr_user", JSON.stringify(userProfile));

        // Force password change on first login (SOC 2 / DPDPA compliant)
        if (resp.must_change_password) {
          setLoading(false);
          router.push("/login/change-password");
          return;
        }

        // Always route to hospital page (location picker handles routing)
        router.push(`/ehr/${resp.hospital_slug}`);
        return;
      } catch {
        // Not a hospital user — fall through to system admin
        if (isHospitalUser) throw new Error("Hospital login failed");
      }

      // Try system admin login
      const sysResp = await systemLogin(email, password);
      localStorage.setItem("access_token", sysResp.access);
      localStorage.setItem("refresh_token", sysResp.refresh);
      localStorage.setItem("user", JSON.stringify(sysResp.user));
      router.push(systemRedirectPath(sysResp.user));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Invalid credentials. Please try again."
      );
      setLoading(false);
    }
  };

  // =========================================================================
  // Render
  // =========================================================================
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/30 p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3">
          <Image
            src="/logo-light.png"
            alt="OneMed EHR"
            width={200}
            height={80}
            priority
            className="dark:hidden"
          />
          <Image
            src="/logo-dark.png"
            alt="OneMed EHR"
            width={200}
            height={80}
            priority
            className="hidden dark:block"
          />
          <Badge variant="secondary" className="text-xs font-normal px-3">
            Electronic Health Record
          </Badge>
        </div>

        <Card className="shadow-lg border-border/50">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl">Welcome back</CardTitle>
            <CardDescription>
              Enter your hospital or admin credentials
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                  <span className="mt-0.5 shrink-0">⚠</span>
                  <span>{error}</span>
                </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@hospital.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="pl-9"
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    href="/login/forgot-password"
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                    tabIndex={-1}
                  >
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="pl-9 pr-10"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} OneMedAI · All rights reserved
        </p>
      </div>
    </div>
  );
}
